import React from 'react';
import { User, Heart, Brain, FileText, BookOpen, Activity } from 'lucide-react';

const MentalHealthIntakeDisplay = ({ formData }) => {
  // Default data structure if no formData is provided
  const data = formData? formData: {
      patient_intake_form: {
        patientDetails: {
          firstName: '',
          lastName: '',
          age: '',
          city: '',
          clientId: '',
          counsellorName: '',
          dateOfIntakeSession: '',
          gender: { male: false, female: false, other: false },
          languageSpoken: { english: false, urdu: false, other: false, otherText: '' },
        },
        presentingComplaint: '',
        durationOfConcern: {
          lessThan2Weeks: false,
          twoTo6Weeks: false,
          moreThanMonth: false,
        },
        immediateRisk: {
          none: false,
          mildFeelingOverwhelmed: false,
          moderateEmotionalDistress: false,
          highHarmToSelfOthers: false,
        },
        natureOfConcern: {
          abuse: {
            emotionalVerbalAbuse: false,
            physicalAbuse: false,
            sexualAbuseAssault: false,
            harassmentWorkplaceSchoolOnline: false,
            traumaHistoryPastOngoing: false,
            other: false,
            otherText: '',
          },
          psychological: {
            anxietyPanicWorry: false,
            lowMoodDepressive: false,
            stressBurnout: false,
            lonelinessIsolation: false,
            lowSelfWorthConfidence: false,
            bodyImageSelfEsteem: false,
            sleepAppetiteDisturbances: false,
            thoughtsSelfHarmHopelessness: false,
          },
          family: {
            conflictWithFamily: false,
            relationshipWithCaregivers: false,
            parentingConcerns: false,
            lackOfSocialSupport: false,
            peerPressureSocialAnxiety: false,
            financialStrain: false,
          },
          marital: {
            communicationDifficulties: false,
            conflictResolution: false,
            infidelityTrustIssues: false,
            separationDivorceAdjustment: false,
            balancingRolesExpectations: false,
          },
          adjustment: {
            griefLoss: false,
            lifeTransitionsRelocation: false,
            chronicIllnessPain: false,
            identityConcerns: false,
            existentialSpiritualCrisis: false,
          },
          academic: {
            performanceConcerns: false,
            difficultyFocusing: false,
            stressAroundTests: false,
            absenteeism: false,
            careerDirection: false,
            studyProductivitySkills: false,
            adjustmentToNewSchool: false,
            transitionIssues: false,
          },
          behavioral: {
            aggressiveWithdrawnBehavior: false,
            difficultyEmotionalRegulation: false,
            riskTakingImpulsiveBehaviors: false,
            habitualCompulsiveBehaviors: false,
            socialInterpersonalDifficulties: false,
            substanceUseMisuse: false,
            managingAuthorityFigures: false,
          },
        },
      },
      therapist_form: {
        basicInfo: {
          therapistName: '',
          firstName: '',
          lastName: '',
          age: '',
          dateOfIntake: '',
          educationLevel: '',
          occupation: '',
          sessionNumber: '',
          spouseName: '',
          primaryContactNumber: '',
          secondaryContactNumber: '',
          gender: { male: false, female: false, other: false },
          maritalStatus: { single: false, married: false, widowed: false, divorced: false },
          referralMadeBy: { parent: false, self: false, counselor: false },
        },
        briefPsychosocialHistory: {
          familyBackground: '',
          medicalHistory: '',
          academicWorkHistory: '',
          lifestyleFactors: '',
          socialLifeRelationships: '',
        },
        mentalHealthConcerns: {
          behavioral: { impulsivity: false, aggression: false, withdrawal: false, selfHarm: false },
          cognitive: { concentrationIssues: false, overthinking: false, negativeThinking: false },
          emotional: { anxiety: false, depression: false, anger: false, moodSwings: false },
          relational: { communicationProblems: false, conflictAtHomeWork: false, isolation: false },
          suicidalIdeation: '',
          traumaHistory: '',
        },
        presentingConcerns: {
          complaintDescription: '',
          clientUnderstandingOfCause: '',
          currentMedication: '',
          previousHelpSought: '',
        },
        therapistObservations: {
          clientOrientation: '',
          clientAffectInsightMotivation: '',
          immediateRedFlags: '',
          riskLevel: 'none',
        },
        summaryAndPlan: {
          tentativeDiagnosis: '',
          outlineShortTermGoals: '',
          howWillYouKnowGoalsAchieved: '',
          numberOfSessionsRequired: '',
          additionalComments: '',
          referralFollowUpRequired: { yes: false, no: false },
        },
        wellBeingSelfAssessment: {
          overallSatisfactionWithLife: 1,
          satisfactionWithJobStudies: 1,
          satisfactionWithPersonalRelationships: 1,
          satisfactionWithPhysicalHealth: 1,
          satisfactionWithMentalHealth: 1,
          satisfactionWithFinancialSituation: 1,
          satisfactionWithLivingEnvironment: 1,
        },
      },
  };

  const patient = data.patient_intake_form;
  const therapist = data.therapist_form;

  console.log(patient)

  const getSelectedGender = (genderObj) => {
    if (genderObj.male) return 'Male';
    if (genderObj.female) return 'Female';
    if (genderObj.other) return 'Other';
    return 'Not specified';
  };

  const getSelectedLanguages = (langObj) => {
    const langs = [];
    if (langObj.english) langs.push('English');
    if (langObj.urdu) langs.push('Urdu');
    if (langObj.other) langs.push(langObj.otherText || 'Other');
    return langs.length > 0 ? langs.join(', ') : 'Not specified';
  };

  const getDuration = (durationObj) => {
    if (durationObj.lessThan2Weeks) return 'Less than 2 weeks';
    if (durationObj.twoTo6Weeks) return '2-6 weeks';
    if (durationObj.moreThanMonth) return 'More than a month';
    return 'Not specified';
  };

  const getRiskLevel = (riskObj) => {
    if (riskObj.none) return 'None';
    if (riskObj.mildFeelingOverwhelmed) return 'Mild - feeling overwhelmed';
    if (riskObj.moderateEmotionalDistress) return 'Moderate emotional distress';
    if (riskObj.highHarmToSelfOthers) return 'High risk of harm to self or others';
    return 'Not specified';
  };

  const getMaritalStatus = (statusObj) => {
    if (statusObj.single) return 'Single';
    if (statusObj.married) return 'Married';
    if (statusObj.widowed) return 'Widowed';
    if (statusObj.divorced) return 'Divorced';
    return 'Not specified';
  };

  const getReferralSource = (referralObj) => {
    if (referralObj.parent) return 'Parent';
    if (referralObj.self) return 'Self';
    if (referralObj.counselor) return 'Counselor';
    return 'Not specified';
  };

  const getSelectedItems = (obj, labels) => {
    const selected = [];
    Object.entries(obj).forEach(([key, value]) => {
      if (value === true && labels[key]) {
        selected.push(labels[key]);
      }
    });
    return selected.length > 0 ? selected : ['None selected'];
  };

  return (
    <div style={{ 
      minHeight: '100vh',
    }}>
      <style>{`
        .container {
          max-width: 1200px;
          margin: 0 auto;
          overflow: hidden;
        }

        .content {
          padding: 40px;
        }

        .section {
          margin-bottom: 40px;
          padding: 30px;
          background: #f8f9fa;
          border-radius: 12px;
          border-left: 4px solid #24AAA4;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #e9ecef;
        }

        .section-icon {
          width: 32px;
          height: 32px;
          color: #24AAA4;
        }

        .section-title {
          margin: 0;
          font-size: 20px;
          font-weight: 700;
          color: #2d3748;
        }

        .subsection {
          margin-bottom: 24px;
        }

        .subsection-title {
          font-size: 16px;
          font-weight: 600;
          color: #4a5568;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid #cbd5e0;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }

        .info-item {
          background: white;
          padding: 16px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .info-label {
          font-size: 12px;
          font-weight: 600;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }

        .info-value {
          font-size: 16px;
          color: #2d3748;
          font-weight: 500;
        }

        .info-value.empty {
          color: #a0aec0;
          font-style: italic;
        }

        .text-block {
          background: white;
          padding: 12px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          margin-bottom: 16px;
        }

        .text-block-label {
          font-size: 12px;
          font-weight: 600;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .text-block-content {
          font-size: 14px;
          line-height: 1.6;
          color: #2d3748;
        }

        .concerns-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 12px;
        }

        .concern-tag {
          background: #24AAA4;
          color: white;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
        }

        .concern-tag.none {
          background: #e2e8f0;
          color: #718096;
        }

        .risk-badge {
          display: inline-block;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 14px;
        }

        .risk-none { background: #c6f6d5; color: #22543d; }
        .risk-mild { background: #fef5e7; color: #975a16; }
        .risk-moderate { background: #fed7d7; color: #742a2a; }
        .risk-high { background: #fc8181; color: #742a2a; }

        .rating-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .rating-item {
          background: white;
          padding: 16px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .rating-label {
          font-size: 13px;
          color: #4a5568;
          margin-bottom: 8px;
          font-weight: 500;
        }

        .rating-value {
          display: flex;
          gap: 4px;
          align-items: center;
        }

        .rating-circle {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
        }

        .rating-1 { background: #fc8181; color: white; }
        .rating-2 { background: #f6ad55; color: white; }
        .rating-3 { background: #f6e05e; color: #744210; }
        .rating-4 { background: #9ae6b4; color: #22543d; }
        .rating-5 { background: #68d391; color: white; }

        @media (max-width: 768px) {
          .content {
            padding: 20px;
          }
          
          .section {
            padding: 20px;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="container">
        <div className="content">
          {/* Patient Details Section */}
          <div className="section">
            <div className="section-header">
              <User className="section-icon" />
              <h2 className="section-title">Patient Details</h2>
            </div>
            
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Full Name</div>
                <div className="info-value">{patient.patientDetails.firstName} {patient.patientDetails.lastName}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Age</div>
                <div className="info-value">{patient.patientDetails.age || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Gender</div>
                <div className="info-value">{getSelectedGender(patient.patientDetails.gender)}</div>
              </div>
              <div className="info-item">
                <div className="info-label">City</div>
                <div className="info-value">{patient.patientDetails.city || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Client ID</div>
                <div className="info-value">{patient.patientDetails.clientId || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Counsellor Name</div>
                <div className="info-value">{patient.patientDetails.counsellorName || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Date of Intake</div>
                <div className="info-value">{patient.patientDetails.dateOfIntakeSession || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Languages Spoken</div>
                <div className="info-value">{getSelectedLanguages(patient.patientDetails.languageSpoken)}</div>
              </div>
            </div>
          </div>

          {/* Presenting Complaint Section */}
          <div className='section'>
            <div className="section-header">
              <Heart className="section-icon" />
              <h2 className="section-title">Presenting Complaint & Risk Assessment</h2>
            </div>
            <div className='text-block'>
              <div className="text-block-label">Presenting Complaint</div>
              <div className="text-block-content">
              {patient.presentingComplaint || 'Not provided'}
              </div>
            </div>
            
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Duration of Concern</div>
                <div className="info-value">{getDuration(patient.durationOfConcern)}</div>
                </div>
                <div className="info-item">
                <div className="info-label">Immediate Risk Level</div>
                <div className="info-value">
                    <span className={`risk-badge risk-${getRiskLevel(patient.immediateRisk).toLowerCase().split(' ')[0]}`}>
                    {getRiskLevel(patient.immediateRisk)}
                    </span>
                </div>
              </div>
            </div>
          </div>

          {/* Nature of Concerns Section */}
          <div className="section">
            <div className="section-header">
              <Brain className="section-icon" />
              <h2 className="section-title">Nature of Concerns</h2>
            </div>

            <div className="subsection">
              <div className="subsection-title">Abuse Related</div>
              <div className="concerns-list">
                {getSelectedItems(patient.natureOfConcern.abuse, {
                  emotionalVerbalAbuse: "Emotional/Verbal Abuse",
                  physicalAbuse: "Physical Abuse",
                  sexualAbuseAssault: "Sexual Abuse/Assault",
                  harassmentWorkplaceSchoolOnline: "Harassment",
                  traumaHistoryPastOngoing: "Trauma History",
                  other: patient.natureOfConcern.abuse.otherText || "Other"
                }).map((item, i) => (
                  <span key={i} className={`concern-tag ${item === 'None selected' ? 'none' : ''}`}>{item}</span>
                ))}
              </div>
            </div>

            <div className="subsection">
              <div className="subsection-title">Psychological Concerns</div>
              <div className="concerns-list">
                {getSelectedItems(patient.natureOfConcern.psychological, {
                  anxietyPanicWorry: "Anxiety/Panic/Worry",
                  lowMoodDepressive: "Low Mood/Depression",
                  stressBurnout: "Stress/Burnout",
                  lonelinessIsolation: "Loneliness/Isolation",
                  lowSelfWorthConfidence: "Low Self-Worth",
                  bodyImageSelfEsteem: "Body Image Issues",
                  sleepAppetiteDisturbances: "Sleep/Appetite Issues",
                  thoughtsSelfHarmHopelessness: "Self-Harm Thoughts"
                }).map((item, i) => (
                  <span key={i} className={`concern-tag ${item === 'None selected' ? 'none' : ''}`}>{item}</span>
                ))}
              </div>
            </div>

            <div className="subsection">
              <div className="subsection-title">Family & Social Concerns</div>
              <div className="concerns-list">
                {getSelectedItems(patient.natureOfConcern.family, {
                  conflictWithFamily: "Family Conflict",
                  relationshipWithCaregivers: "Caregiver Relationship",
                  parentingConcerns: "Parenting Concerns",
                  lackOfSocialSupport: "Lack of Support",
                  peerPressureSocialAnxiety: "Peer Pressure/Social Anxiety",
                  financialStrain: "Financial Strain"
                }).map((item, i) => (
                  <span key={i} className={`concern-tag ${item === 'None selected' ? 'none' : ''}`}>{item}</span>
                ))}
              </div>
            </div>

            <div className="subsection">
              <div className="subsection-title">Marital Concerns</div>
              <div className="concerns-list">
                {getSelectedItems(patient.natureOfConcern.marital, {
                  communicationDifficulties: "Communication Issues",
                  conflictResolution: "Conflict Resolution",
                  infidelityTrustIssues: "Trust Issues",
                  separationDivorceAdjustment: "Separation/Divorce",
                  balancingRolesExpectations: "Role Balance"
                }).map((item, i) => (
                  <span key={i} className={`concern-tag ${item === 'None selected' ? 'none' : ''}`}>{item}</span>
                ))}
              </div>
            </div>

            <div className="subsection">
              <div className="subsection-title">Academic Concerns</div>
              <div className="concerns-list">
                {getSelectedItems(patient.natureOfConcern.academic, {
                  performanceConcerns: "Performance Issues",
                  difficultyFocusing: "Focus Difficulty",
                  stressAroundTests: "Test Stress",
                  absenteeism: "Absenteeism",
                  careerDirection: "Career Direction",
                  studyProductivitySkills: "Study Skills",
                  adjustmentToNewSchool: "School Adjustment",
                  transitionIssues: "Transition Issues"
                }).map((item, i) => (
                  <span key={i} className={`concern-tag ${item === 'None selected' ? 'none' : ''}`}>{item}</span>
                ))}
              </div>
            </div>

            <div className="subsection">
              <div className="subsection-title">Behavioral Issues</div>
              <div className="concerns-list">
                {getSelectedItems(patient.natureOfConcern.behavioral, {
                  aggressiveWithdrawnBehavior: "Aggressive/Withdrawn",
                  difficultyEmotionalRegulation: "Emotional Regulation",
                  riskTakingImpulsiveBehaviors: "Impulsive Behaviors",
                  habitualCompulsiveBehaviors: "Compulsive Behaviors",
                  socialInterpersonalDifficulties: "Social Difficulties",
                  substanceUseMisuse: "Substance Use",
                  managingAuthorityFigures: "Authority Issues"
                }).map((item, i) => (
                  <span key={i} className={`concern-tag ${item === 'None selected' ? 'none' : ''}`}>{item}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Therapist Intake Section */}
          <div className="section">
            <div className="section-header">
              <User className="section-icon" />
              <h2 className="section-title">Therapist Intake</h2>
            </div>
            
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Full Name</div>
                <div className="info-value">{therapist.basicInfo.firstName} {therapist.basicInfo.lastName}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Age</div>
                <div className="info-value">{therapist.basicInfo.age || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Gender</div>
                <div className="info-value">{getSelectedGender(therapist.basicInfo.gender)}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Marital Status</div>
                <div className="info-value">{getMaritalStatus(therapist.basicInfo.maritalStatus)}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Education Level</div>
                <div className="info-value">{therapist.basicInfo.educationLevel || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Occupation</div>
                <div className="info-value">{patient.patientDetails.occupation || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Therapist Name</div>
                <div className="info-value">{therapist.basicInfo.therapistName || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Spouse Name</div>
                <div className="info-value">{therapist.basicInfo.spouseName || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Primary Contact</div>
                <div className="info-value">{therapist.basicInfo.primaryContactNumber || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Secondary Contact</div>
                <div className="info-value">{therapist.basicInfo.secondaryContactNumber || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Date of Intake</div>
                <div className="info-value">{therapist.basicInfo.dateOfIntake || 'Not provided'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Refer By</div>
                <div className="info-value">{getReferralSource(therapist.basicInfo.referralMadeBy)}</div>
              </div>
              
            </div>
          </div>

          {/* Brief  Section */}
          <div className="section">
            <div className="section-header">
              <Heart className="section-icon" />
              <h2 className="section-title">Brief Psychosocial History</h2>
            </div>


            <div className="text-block">
              <div className="text-block-label">Family Background</div>
              <div className="text-block-content">{therapist.briefPsychosocialHistory.familyBackground || 'Not provided'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Medical History</div>
              <div className="text-block-content">{therapist.briefPsychosocialHistory.medicalHistory || 'Not provided'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Academic & Work History</div>
              <div className="text-block-content">{therapist.briefPsychosocialHistory.academicWorkHistory || 'Not provided'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Lifestyle Factors</div>
              <div className="text-block-content">{therapist.briefPsychosocialHistory.lifestyleFactors || 'Not provided'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Social Life & Relationships</div>
              <div className="text-block-content">{therapist.briefPsychosocialHistory.socialLifeRelationships || 'Not provided'}</div>
            </div>

            <div className="subsection-title">Mental Health Concerns</div>
            <div className="subsection">
              <div className="subsection-title">Behavioral</div>
              <div className="concerns-list">
                {getSelectedItems(therapist.mentalHealthConcerns.behavioral, {
                  impulsivity: "Impulsivity",
                  aggression: "Aggression",
                  withdrawal: "Withdrawal",
                  selfHarm: "Self-Harm"
                }).map((item, i) => (
                  <span key={i} className={`concern-tag ${item === 'None selected' ? 'none' : ''}`}>{item}</span>
                ))}
              </div>
            </div>

            <div className="subsection">
              <div className="subsection-title">Cognitive</div>
              <div className="concerns-list">
                {getSelectedItems(therapist.mentalHealthConcerns.cognitive, {
                  concentrationIssues: "Concentration Issues",
                  overthinking: "Overthinking",
                  negativeThinking: "Negative Thinking"
                }).map((item, i) => (
                  <span key={i} className={`concern-tag ${item === 'None selected' ? 'none' : ''}`}>{item}</span>
                ))}
              </div>
            </div>

            <div className="subsection">
              <div className="subsection-title">Emotional</div>
              <div className="concerns-list">
                {getSelectedItems(therapist.mentalHealthConcerns.emotional, {
                  anxiety: "Anxiety",
                  depression: "Depression",
                  anger: "Anger",
                  moodSwings: "Mood Swings"
                }).map((item, i) => (
                  <span key={i} className={`concern-tag ${item === 'None selected' ? 'none' : ''}`}>{item}</span>
                ))}
              </div>
            </div>

            <div className="subsection">
              <div className="subsection-title">Relational</div>
              <div className="concerns-list">
                {getSelectedItems(therapist.mentalHealthConcerns.relational, {
                  communicationProblems: "Communication Problems",
                  conflictAtHomeWork: "Conflict at Home/Work",
                  isolation: "Isolation"
                }).map((item, i) => (
                  <span key={i} className={`concern-tag ${item === 'None selected' ? 'none' : ''}`}>{item}</span>
                ))}
              </div>
            </div>

            <div className="text-block">
              <div className="text-block-label">Suicidal Ideation</div>
              <div className="text-block-content">{therapist.mentalHealthConcerns.suicidalIdeation || 'Not reported'}</div>
            </div>

            <div className="text-block">
              <div className="text-block-label">Trauma History</div>
              <div className="text-block-content">{therapist.mentalHealthConcerns.traumaHistory || 'Not reported'}</div>
            </div>

            <div className="subsection-title">Presenting Concerns</div>
            <div className="text-block">
              <div className="text-block-label">Complaint Description</div>
              <div className="text-block-content">{therapist.presentingConcerns.complaintDescription || 'Not provided'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Client's Understanding of Cause</div>
              <div className="text-block-content">{therapist.presentingConcerns.clientUnderstandingOfCause || 'Not provided'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Current Medication</div>
              <div className="text-block-content">{therapist.presentingConcerns.currentMedication || 'None reported'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Previous Help Sought</div>
              <div className="text-block-content">{therapist.presentingConcerns.previousHelpSought || 'None reported'}</div>
            </div>
          </div>

          {/* Therapist Observations Section */}
          <div className="section">
            <div className="section-header">
              <Brain className="section-icon" />
              <h2 className="section-title">Therapist Observations</h2>
            </div>

            <div className="text-block">
              <div className="text-block-label">Client Orientation</div>
              <div className="text-block-content">{therapist.therapistObservations.clientOrientation || 'Not provided'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Client Affect, Insight & Motivation</div>
              <div className="text-block-content">{therapist.therapistObservations.clientAffectInsightMotivation || 'Not provided'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Immediate Red Flags</div>
              <div className="text-block-content">{therapist.therapistObservations.immediateRedFlags || 'None reported'}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Risk Level</div>
              <div className="info-value">
                <span className={`risk-badge risk-${therapist.therapistObservations.riskLevel || 'none'}`}>
                  {therapist.therapistObservations.riskLevel ? therapist.therapistObservations.riskLevel.charAt(0).toUpperCase() + therapist.therapistObservations.riskLevel.slice(1) : 'Not assessed'}
                </span>
              </div>
            </div>
          </div>

          {/* Well-being Assessment Section */}
          <div className="section">
            <div className="section-header">
              <Activity className="section-icon" />
              <h2 className="section-title">Well-being Self Assessment</h2>
            </div>

            <div className="rating-container">
              <div className="rating-item">
                <div className="rating-label">Overall Life Satisfaction</div>
                <div className="rating-value">
                  <span className={`rating-circle rating-${therapist.wellBeingSelfAssessment.overallSatisfactionWithLife}`}>
                    {therapist.wellBeingSelfAssessment.overallSatisfactionWithLife}
                  </span>
                  <span>/ 5</span>
                </div>
              </div>
              <div className="rating-item">
                <div className="rating-label">Job/Studies Satisfaction</div>
                <div className="rating-value">
                  <span className={`rating-circle rating-${therapist.wellBeingSelfAssessment.satisfactionWithJobStudies}`}>
                    {therapist.wellBeingSelfAssessment.satisfactionWithJobStudies}
                  </span>
                  <span>/ 5</span>
                </div>
              </div>
              <div className="rating-item">
                <div className="rating-label">Personal Relationships</div>
                <div className="rating-value">
                  <span className={`rating-circle rating-${therapist.wellBeingSelfAssessment.satisfactionWithPersonalRelationships}`}>
                    {therapist.wellBeingSelfAssessment.satisfactionWithPersonalRelationships}
                  </span>
                  <span>/ 5</span>
                </div>
              </div>
              <div className="rating-item">
                <div className="rating-label">Physical Health</div>
                <div className="rating-value">
                  <span className={`rating-circle rating-${therapist.wellBeingSelfAssessment.satisfactionWithPhysicalHealth}`}>
                    {therapist.wellBeingSelfAssessment.satisfactionWithPhysicalHealth}
                  </span>
                  <span>/ 5</span>
                </div>
              </div>
              <div className="rating-item">
                <div className="rating-label">Mental Health</div>
                <div className="rating-value">
                  <span className={`rating-circle rating-${therapist.wellBeingSelfAssessment.satisfactionWithMentalHealth}`}>
                    {therapist.wellBeingSelfAssessment.satisfactionWithMentalHealth}
                  </span>
                  <span>/ 5</span>
                </div>
              </div>
              <div className="rating-item">
                <div className="rating-label">Financial Situation</div>
                <div className="rating-value">
                  <span className={`rating-circle rating-${therapist.wellBeingSelfAssessment.satisfactionWithFinancialSituation}`}>
                    {therapist.wellBeingSelfAssessment.satisfactionWithFinancialSituation}
                  </span>
                  <span>/ 5</span>
                </div>
              </div>
              <div className="rating-item">
                <div className="rating-label">Living Environment</div>
                <div className="rating-value">
                  <span className={`rating-circle rating-${therapist.wellBeingSelfAssessment.satisfactionWithLivingEnvironment}`}>
                    {therapist.wellBeingSelfAssessment.satisfactionWithLivingEnvironment}
                  </span>
                  <span>/ 5</span>
                </div>
              </div>
            </div>
          </div>

          {/* Summary and Plan Section */}
          <div className="section">
            <div className="section-header">
              <BookOpen className="section-icon" />
              <h2 className="section-title">Summary & Treatment Plan</h2>
            </div>

            <div className="text-block">
              <div className="text-block-label">Tentative Diagnosis</div>
              <div className="text-block-content">{therapist.summaryAndPlan.tentativeDiagnosis || 'Not provided'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Short-Term Goals</div>
              <div className="text-block-content">{therapist.summaryAndPlan.outlineShortTermGoals || 'Not provided'}</div>
            </div>
            <div className="text-block">
              <div className="text-block-label">Success Indicators</div>
              <div className="text-block-content">{therapist.summaryAndPlan.howWillYouKnowGoalsAchieved || 'Not provided'}</div>
            </div>
            
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Number of Sessions Required</div>
                <div className="info-value">{therapist.summaryAndPlan.numberOfSessionsRequired || 'Not specified'}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Referral/Follow-up Required</div>
                <div className="info-value">
                  {therapist.summaryAndPlan.referralFollowUpRequired.yes ? 'Yes' : 
                  therapist.summaryAndPlan.referralFollowUpRequired.no ? 'No' : 'Not specified'}
                </div>
              </div>
            </div>

            {therapist.summaryAndPlan.additionalComments && (
              <div className="text-block">
                <div className="text-block-label">Additional Comments</div>
                <div className="text-block-content">{therapist.summaryAndPlan.additionalComments}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentalHealthIntakeDisplay;
